import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/NewButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, History } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/api-client';
import { toast } from 'react-hot-toast';
import { PromptCategory, PromptScope, PromptResponse } from './types';
import { PromptVersionHistory } from './PromptVersionHistory';
import { useMatterLabels } from '@/lib/useMatterLabels';
import {
  createMatterPromptLibraryApi,
  type PromptLibraryApi,
} from '@/lib/promptLibraryApi';

const ALL_SCOPE_OPTIONS: { value: PromptScope; label: string; description: string }[] = [
  { value: 'user', label: 'Personal', description: 'Only visible to you' },
  { value: 'matter', label: 'Matter', description: 'Visible to all members of this matter' },
  { value: 'tenant', label: 'Firm', description: 'Visible to everyone in your firm' },
];

export interface EditPromptData {
  promptGroupId: string;
  title: string;
  content: string;
  categoryName: string;
  scope: PromptScope;
  canManage: boolean;
}

interface PromptFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPromptSaved?: () => void;
  prefillContent?: string;
  editPrompt?: EditPromptData;
  /** When omitted on a matter route, a matter-scoped API is created from the route slug or matterSlug. */
  api?: PromptLibraryApi;
  defaultScope?: PromptScope;
  allowedScopes?: PromptScope[];
  matterSlug?: string;
}

export const PromptFormDialog: React.FC<PromptFormDialogProps> = ({
  open,
  onOpenChange,
  onPromptSaved,
  prefillContent = '',
  editPrompt,
  api: apiProp,
  defaultScope = 'user',
  allowedScopes,
  matterSlug,
}) => {
  const routeParams = useParams({ strict: false }) as { slug?: string };
  const slug = matterSlug ?? routeParams.slug;
  const api = useMemo(
    () => apiProp ?? (slug ? createMatterPromptLibraryApi(slug) : null),
    [apiProp, slug],
  );
  const label = useMatterLabels();
  const isEditMode = !!editPrompt;
  const canManage = editPrompt?.canManage ?? false;
  const didInit = useRef(false);

  const scopeOptions = useMemo(() => {
    const allowed = allowedScopes ?? ALL_SCOPE_OPTIONS.map((o) => o.value);
    return ALL_SCOPE_OPTIONS.filter((o) => allowed.includes(o.value));
  }, [allowedScopes]);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [scope, setScope] = useState<PromptScope>(defaultScope);
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [newCategoryInput, setNewCategoryInput] = useState<string | null>(null);
  const [addedName, setAddedName] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const uniqueCategoryNames = useMemo(() => {
    const names = new Set(categories.map((c) => c.name));
    if (addedName) names.add(addedName);
    return Array.from(names).sort();
  }, [categories, addedName]);

  const canSave = !!(title.trim() && content.trim() && categoryName);

  const fetchCategories = useCallback(async () => {
    if (!api) return;
    setIsLoadingCategories(true);
    try {
      const data = await api.listCategories();
      setCategories(data);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  }, [api]);

  useEffect(() => {
    if (!open || !api || didInit.current) return;
    didInit.current = true;

    void fetchCategories();
    if (editPrompt) {
      setTitle(editPrompt.title);
      setContent(editPrompt.content);
      setCategoryName(editPrompt.categoryName);
      setScope(editPrompt.scope);
    } else {
      setContent(prefillContent);
      setScope(defaultScope);
    }
  }, [open, api, editPrompt, prefillContent, defaultScope, fetchCategories]);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setContent('');
      setCategoryName('');
      setScope(defaultScope);
      setNewCategoryInput(null);
      setAddedName(null);
      setShowHistory(false);
      setShowDeleteConfirm(false);
      didInit.current = false;
    }
  }, [open, defaultScope]);

  useEffect(() => {
    if (!isLoadingCategories && uniqueCategoryNames.length === 0) {
      setNewCategoryInput('');
    }
  }, [isLoadingCategories, uniqueCategoryNames.length]);

  const handleAddNewCategoryName = () => {
    if (newCategoryInput === null || !newCategoryInput.trim()) return;
    const name = newCategoryInput.trim();
    setAddedName(name);
    setCategoryName(name);
    setNewCategoryInput(null);
  };

  const resolveCategoryId = async (): Promise<string | null> => {
    if (!api) return null;
    const existing = categories.find((c) => c.name === categoryName && c.scope === scope);
    if (existing) return existing.id;

    try {
      const newCat = await api.createCategory(categoryName, scope);
      setCategories((prev) => [...prev, newCat]);
      return newCat.id;
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to create category');
      return null;
    }
  };

  const handleSave = async () => {
    if (!canSave || !api) return;
    setIsSaving(true);
    try {
      const categoryId = await resolveCategoryId();
      if (!categoryId) {
        setIsSaving(false);
        return;
      }

      if (isEditMode) {
        await api.updatePrompt(editPrompt!.promptGroupId, {
          title: title.trim(),
          content: content.trim(),
          category_id: categoryId,
        });
        toast.success('Prompt updated');
      } else {
        await api.createPrompt({
          title: title.trim(),
          content: content.trim(),
          category_id: categoryId,
        });
        toast.success('Prompt saved to library');
      }
      onPromptSaved?.();
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !api) return;
    setIsDeleting(true);
    try {
      await api.deletePrompt(editPrompt!.promptGroupId);
      toast.success('Prompt deleted');
      onPromptSaved?.();
      onOpenChange(false);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Failed to delete prompt');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestore = useCallback((version: PromptResponse) => {
    setTitle(version.title);
    setContent(version.content);
    setCategoryName(version.category_name);
    toast.success(`Restored to version ${version.version}`);
  }, []);

  if (!api) return null;

  const showScopePicker = scopeOptions.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('transition-all', showHistory ? 'sm:max-w-[1100px]' : 'sm:max-w-[640px]')}>
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Prompt' : 'Add to Prompt Library'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update your prompt' : 'Save a reusable prompt to your library'}
          </DialogDescription>
        </DialogHeader>

        <div className={cn('flex gap-4', showHistory && 'max-h-[60vh]')}>
          <div className={cn('space-y-4 py-2', showHistory ? 'flex-1 min-w-0 overflow-y-auto' : 'w-full')}>
            <div className="space-y-2">
              <Label htmlFor="prompt-title" className="md:block md:pb-0.5">Title</Label>
              <Input
                id="prompt-title"
                placeholder={label('summarise-witness-placeholder')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={256}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt-content" className="md:block md:pb-0.5">Prompt</Label>
              <Textarea
                id="prompt-content"
                placeholder="Enter the prompt text..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={50000}
                className="min-h-[120px] max-h-[300px] resize-y"
              />
              <p className="text-xs text-slate-400 text-right">
                {content.length.toLocaleString()} / 50,000
              </p>
            </div>

            <div className="space-y-2">
              <Label className="md:block md:pb-0.5">Visibility</Label>
              {showScopePicker ? (
                <>
                  <div className="flex gap-2">
                    {scopeOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={cn(
                          'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
                          scope === opt.value
                            ? 'border-blue-300 bg-blue-50 text-blue-700'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                        )}
                        onClick={() => setScope(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    {scopeOptions.find((o) => o.value === scope)?.description}
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  {scopeOptions[0]?.label ?? 'Firm'}
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {scopeOptions[0]?.description ?? 'Visible to everyone in your firm'}
                  </span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="md:block md:pb-0.5">Category</Label>
              {newCategoryInput !== null ? (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="New category name"
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddNewCategoryName();
                      }
                      if (e.key === 'Escape' && uniqueCategoryNames.length > 0) {
                        setNewCategoryInput(null);
                      }
                    }}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    text="Add"
                    onClick={handleAddNewCategoryName}
                    disabled={!newCategoryInput.trim()}
                  />
                  {uniqueCategoryNames.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      text="Cancel"
                      onClick={() => setNewCategoryInput(null)}
                    />
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select value={categoryName} onValueChange={setCategoryName}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={isLoadingCategories ? 'Loading...' : 'Select a category'} />
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueCategoryNames.map((name) => (
                        <SelectItem key={name} value={name} className="hover:bg-stone-100 cursor-pointer">
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={Plus}
                    onClick={() => setNewCategoryInput('')}
                    title="Create new category"
                  />
                </div>
              )}
            </div>
          </div>

          {showHistory && editPrompt && (
            <PromptVersionHistory
              api={api}
              promptGroupId={editPrompt.promptGroupId}
              onRestore={handleRestore}
            />
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            {isEditMode && canManage && (
              <Button
                variant="destructive"
                text="Delete"
                icon={Trash2}
                onClick={() => setShowDeleteConfirm(true)}
              />
            )}
            {isEditMode && canManage && (
              <Button
                variant="outline"
                text={showHistory ? 'Hide History' : 'History'}
                icon={History}
                onClick={() => setShowHistory(!showHistory)}
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" text="Cancel" onClick={() => onOpenChange(false)} />
            <Button
              text={isEditMode ? 'Update Prompt' : 'Save Prompt'}
              loadingText={isEditMode ? 'Updating...' : 'Saving...'}
              loading={isSaving}
              disabled={!canSave}
              onClick={handleSave}
            />
          </div>
        </DialogFooter>
      </DialogContent>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Prompt</DialogTitle>
            <DialogDescription>
              This prompt and all its version history will be permanently deleted. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" text="Cancel" onClick={() => setShowDeleteConfirm(false)} />
            <Button
              variant="destructive"
              text="Delete"
              loadingText="Deleting"
              loading={isDeleting}
              onClick={handleDelete}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
